import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { InjectionKey, Plugin } from 'vue';
import { getCurrentInstance, inject } from 'vue';
import type { Router } from 'vue-router';
import { useRouter } from 'vue-router';
import type { ILogger } from '../logger';

export interface IApplicationLoggerOptions {
  userId?: string;
  connectionString: string;
}
class ConsoleLogger implements ILogger {
  logPageView() {
    //
  }
  logInformation(msg: string) {
    console.log(msg);
  }
  logWarning(msg: string) {
    console.warn(msg);
  }
  logError(msg: string) {
    console.error(msg);
  }
  authenticate(userid: string) {
    //
  }
}
export class ApplicationInsightsLogger implements ILogger {
  appInsights: ApplicationInsights | null;
  attachedToRouter: boolean;

  constructor(options: IApplicationLoggerOptions) {
    const { connectionString, userId } = options;
    this.attachedToRouter = false;
    this.appInsights = connectionString ? new ApplicationInsights({ config: { connectionString } }) : null;
    this.appInsights?.loadAppInsights();
    this.appInsights?.trackPageView();
    if (userId) {
      this.authenticate(userId || '');
    }
  }

  authenticate(userId: string) {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  logPageView() {
    this.appInsights?.trackPageView();
  }

  logInformation(message: string) {
    this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Information });
  }

  logWarning(message: string) {
    this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Warning });
  }

  logError(message: string) {
    this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Error });
  }
  attach(router: Router) {
    if (!this.attachedToRouter) {
      this.attachedToRouter = true;
      router.afterEach(() => this.logPageView());
    }
  }
}

const APP_INSIGHTS_SYMBOL: InjectionKey<ApplicationInsightsLogger> = Symbol('app insights');
// cache last logger for instances where the "currentInstance" isn't available, but the logger has already been initialized
// this is particularly useful for route guards and this is how pinia gets around currentInstance issues
let currentLogger: ApplicationInsightsLogger | undefined;

export const appInsightsLoggerPlugin: Plugin = {
  install: (app, options: IApplicationLoggerOptions) => {
    const logger = new ApplicationInsightsLogger(options);
    app.config.globalProperties.$appInsightsLogger = logger;
    app.provide(APP_INSIGHTS_SYMBOL, logger);
  },
};

export const useAppInsightsLogger = (): ILogger => {
  const currentInstance = getCurrentInstance();
  const logger = currentInstance ? inject(APP_INSIGHTS_SYMBOL) : currentLogger;
  if (!logger) {
    console.warn(`Can't find an active logger.  Are you sure you initialized the app insights logger?`);
    return new ConsoleLogger();
  }

  currentLogger = logger;

  if (currentInstance) {
    const router = useRouter();
    logger.attach(router);
  }
  return logger;
};
